import React, { ReactNode } from "react";
import {
  Button,
  ButtonProps,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import { Avatar } from "@outpave/ds";
import { logoLink } from "components/common/RenderMerchantDisplay";
import { useColorScheme } from "context/ColorSchemeContext";
import { Inline, Stack } from "components/layout/reusableLayouts";

const ELLIPSIS = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
} as const;

interface IntegrationMarketplaceCardProps {
  /** The provider's web domain, which its logo is looked up by. */
  domain?: string;
  title: string;
  subtitle?: string;
  /** A line under the header, such as what an available provider does. */
  description?: string;
  /** Chips that qualify a connected integration. */
  badges?: ReactNode;
  actionLabel: string;
  actionVariant: ButtonProps["variant"];
  onAction: () => void;
}

/** One provider in the marketplace: connected, or available to connect. */
const IntegrationMarketplaceCard: React.FC<IntegrationMarketplaceCardProps> = ({
  domain,
  title,
  subtitle,
  description,
  badges,
  actionLabel,
  actionVariant,
  onAction,
}) => {
  const { isDark } = useColorScheme();
  // The card follows the scheme, so a logo drawn for a light ground goes illegible in dark.
  const logoSrc = domain
    ? logoLink(domain, "icon", "lettermark", isDark ? "dark" : "light", 36)
    : undefined;

  return (
    <Card
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        // The grid stretches its cells, so every card in a row matches the
        // tallest and the buttons share a baseline.
        height: "100%",
        minWidth: 0,
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-5)",
          minWidth: 0,
        }}
      >
        <Inline
          gap="var(--space-5)"
          align="center"
          wrap={false}
          sx={{ minWidth: 0 }}
        >
          <Avatar
            src={logoSrc}
            name={title}
            alt={title}
            size="lg"
            variant="rounded"
          />
          <Stack gap="var(--space-1)" sx={{ minWidth: 0 }}>
            <Typography variant="h6" component="h3" title={title} sx={ELLIPSIS}>
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                title={subtitle}
                sx={{ ...ELLIPSIS, color: "var(--color-text-secondary)" }}
              >
                {subtitle}
              </Typography>
            )}
          </Stack>
        </Inline>

        {description && (
          <Typography
            variant="body2"
            sx={{ color: "var(--color-text-secondary)" }}
          >
            {description}
          </Typography>
        )}

        <Inline
          justify={badges ? "between" : "end"}
          align="center"
          gap="var(--space-3)"
          sx={{ marginTop: "auto" }}
        >
          {badges && <Inline gap="var(--space-2)">{badges}</Inline>}
          <Button variant={actionVariant} size="small" onClick={onAction}>
            {actionLabel}
          </Button>
        </Inline>
      </CardContent>
    </Card>
  );
};

export default IntegrationMarketplaceCard;
